<template>
    <div class="recycle-page">
        <!-- 工具条：标题说明 + 刷新 -->
        <div class="recycle-toolbar">
            <div class="recycle-toolbar__info">
                <span class="recycle-toolbar__icon">
                    <el-icon>
                        <Delete />
                    </el-icon>
                </span>
                <span class="recycle-toolbar__meta">
                    <span class="recycle-toolbar__title">回收站</span>
                    <span class="recycle-toolbar__subtitle">已删除的文档与文件保留 180 天，超期后自动清理，请及时还原</span>
                </span>
            </div>
            <div class="recycle-toolbar__actions">
                <el-button class="toolbar-btn toolbar-btn--ghost" :loading="loading" @click="refreshAll">
                    <el-icon v-if="!loading" class="btn-icon">
                        <Refresh />
                    </el-icon>
                    刷新
                </el-button>
            </div>
        </div>

        <!-- 分类切换：文档 / 文件（带数量，代替原标签页） -->
        <div class="recycle-switch">
            <button type="button" class="recycle-switch__item" :class="{ 'is-active': activeTab === 'doc' }"
                @click="activeTab = 'doc'">
                <el-icon class="recycle-switch__icon">
                    <Document />
                </el-icon>
                <span>文档</span>
                <em class="recycle-switch__count">{{ deletedDocTable.length }}</em>
            </button>
            <button type="button" class="recycle-switch__item" :class="{ 'is-active': activeTab === 'file' }"
                @click="activeTab = 'file'">
                <el-icon class="recycle-switch__icon">
                    <FolderOpened />
                </el-icon>
                <span>文件</span>
                <em class="recycle-switch__count">{{ deletedFileTable.length }}</em>
            </button>
        </div>

        <!-- 搜索 + 统计 -->
        <div class="recycle-filter">
            <el-input v-model="keyword" class="recycle-search" placeholder="搜索标题、文集、人员或文件名" clearable>
                <template #prefix>
                    <el-icon>
                        <Search />
                    </el-icon>
                </template>
            </el-input>
            <span class="recycle-filter__count">
                共 {{ currentList.length }} 条{{ keyword ? '（已筛选）' : '' }}
            </span>
        </div>

        <!-- 文档列表 -->
        <section class="recycle-panel" v-if="activeTab === 'doc'">
            <el-empty v-if="filteredDocList.length === 0"
                :description="keyword ? '没有匹配的文档' : '回收站里没有已删除的文档'" />
            <article class="recycle-item" v-for="row in filteredDocList" :key="row.id">
                <span class="recycle-item__icon recycle-item__icon--doc">
                    <el-icon>
                        <Document />
                    </el-icon>
                </span>
                <div class="recycle-item__main">
                    <div class="recycle-item__name" :title="row.title"><em v-if="row.isAi" class="doc-ai-mark">AI</em>{{ row.title }}</div>
                    <div class="recycle-item__meta">
                        <span class="recycle-chip" v-if="row.collectedName">{{ row.collectedName }}</span>
                        <span>拥有者 {{ ownerName(row) }}</span>
                        <span>删除人 {{ deleteName(row) }}</span>
                    </div>
                </div>
                <div class="recycle-item__times">
                    <el-tooltip placement="top" :content="'修改时间：' + (row.updateTime || '—')">
                        <span class="recycle-time">修改 {{ row.relativeUpdateTime || relativeLabel(row.updateTime) }}</span>
                    </el-tooltip>
                    <el-tooltip placement="top" :content="'删除时间：' + (row.deleteTime || '—')">
                        <span class="recycle-time recycle-time--delete">删除
                            {{ row.relativeDeleteTime || relativeLabel(row.deleteTime) }}</span>
                    </el-tooltip>
                </div>
                <div class="recycle-item__actions">
                    <span v-if="leftDays(row.deleteTime) !== null" class="recycle-left"
                        :class="{ 'is-urgent': leftDays(row.deleteTime) <= 7 }">
                        剩余 {{ leftDays(row.deleteTime) }} 天
                    </span>
                    <el-popconfirm width="220" title="确定还原该文档吗？" confirm-button-text="确定还原" cancel-button-text="取消"
                        @confirm="docRestore(row)">
                        <template #reference>
                            <el-button class="restore-btn" type="primary" plain>还原</el-button>
                        </template>
                    </el-popconfirm>
                </div>
            </article>
        </section>

        <!-- 文件列表 -->
        <section class="recycle-panel" v-else>
            <el-empty v-if="filteredFileList.length === 0"
                :description="keyword ? '没有匹配的文件' : '回收站里没有已删除的文件'" />
            <article class="recycle-item" v-for="row in filteredFileList" :key="row.id">
                <span class="recycle-item__icon recycle-item__icon--file">
                    <el-icon>
                        <Files />
                    </el-icon>
                </span>
                <div class="recycle-item__main">
                    <div class="recycle-item__name" :title="row.fileName">{{ row.fileName }}</div>
                    <div class="recycle-item__meta" v-if="fileExt(row.fileName)">
                        <span class="recycle-chip recycle-chip--file">{{ fileExt(row.fileName) }}</span>
                    </div>
                </div>
                <div class="recycle-item__times">
                    <el-tooltip placement="top" :content="'创建时间：' + (row.createTime || '—')">
                        <span class="recycle-time">创建 {{ relativeLabel(row.createTime) }}</span>
                    </el-tooltip>
                    <el-tooltip placement="top" :content="'删除时间：' + (row.deleteTime || '—')">
                        <span class="recycle-time recycle-time--delete">删除 {{ relativeLabel(row.deleteTime) }}</span>
                    </el-tooltip>
                </div>
                <div class="recycle-item__actions">
                    <span v-if="leftDays(row.deleteTime) !== null" class="recycle-left"
                        :class="{ 'is-urgent': leftDays(row.deleteTime) <= 7 }">
                        剩余 {{ leftDays(row.deleteTime) }} 天
                    </span>
                    <el-popconfirm width="220" title="确定还原该文件吗？" confirm-button-text="确定还原" cancel-button-text="取消"
                        @confirm="fileRestore(row)">
                        <template #reference>
                            <el-button class="restore-btn" type="primary" plain>还原</el-button>
                        </template>
                    </el-popconfirm>
                </div>
            </article>
        </section>
    </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import request from '../../../utils/request.js';

// 与后端清理策略保持一致：回收站保留 180 天
const KEEP_DAYS = 180;

export default {
    data() {
        return {
            activeTab: 'doc',
            keyword: '',
            loading: false,
            deletedDocTable: [],
            deletedFileTable: []
        };
    },
    computed: {
        // 关键字过滤（前端过滤，不改动接口返回的排序）
        filteredDocList() {
            const kw = this.keyword.trim().toLowerCase();
            if (!kw) return this.deletedDocTable;
            return this.deletedDocTable.filter(row => {
                const fields = [row.title, row.collectedName, this.ownerName(row), this.deleteName(row)];
                return fields.some(text => String(text || '').toLowerCase().includes(kw));
            });
        },
        filteredFileList() {
            const kw = this.keyword.trim().toLowerCase();
            if (!kw) return this.deletedFileTable;
            return this.deletedFileTable.filter(row => String(row.fileName || '').toLowerCase().includes(kw));
        },
        // 当前分类下展示的记录
        currentList() {
            return this.activeTab === 'doc' ? this.filteredDocList : this.filteredFileList;
        }
    },
    mounted() {
        this.refreshAll();
    },
    methods: {
        refreshAll() {
            this.loading = true;
            return Promise.all([this.loadDeletedDoc(), this.loadDeletedFile()]).finally(() => {
                this.loading = false;
            });
        },
        loadDeletedDoc() {
            return request({
                url: '/doc/deletedList',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0) {
                    this.deletedDocTable = res.data || [];
                }
            });
        },
        loadDeletedFile() {
            return request({
                url: '/file/deletedList',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0) {
                    this.deletedFileTable = res.data || [];
                }
            });
        },
        ownerName(row) {
            return (row.ownerUser && row.ownerUser.realName) || '—';
        },
        deleteName(row) {
            return (row.deleteUser && row.deleteUser.realName) || '—';
        },
        fileExt(fileName) {
            if (!fileName || fileName.lastIndexOf('.') < 0) return '';
            return fileName.slice(fileName.lastIndexOf('.') + 1).toUpperCase();
        },
        // 把时间格式化为「x 天前」这类相对描述，接口已带相对时间的优先用接口值
        relativeLabel(time) {
            if (!time) return '—';
            const date = new Date(String(time).replace(/-/g, '/').replace('T', ' '));
            if (isNaN(date.getTime())) return String(time);
            const diff = Date.now() - date.getTime();
            if (diff < 60 * 1000) return '刚刚';
            if (diff < 60 * 60 * 1000) return Math.floor(diff / 60000) + ' 分钟前';
            if (diff < 24 * 60 * 60 * 1000) return Math.floor(diff / 3600000) + ' 小时前';
            if (diff < 30 * 24 * 60 * 60 * 1000) return Math.floor(diff / 86400000) + ' 天前';
            return String(time).slice(0, 10);
        },
        // 距离超期自动清理还剩多少天
        leftDays(time) {
            if (!time) return null;
            const date = new Date(String(time).replace(/-/g, '/').replace('T', ' '));
            if (isNaN(date.getTime())) return null;
            const used = Math.floor((Date.now() - date.getTime()) / 86400000);
            const left = KEEP_DAYS - used;
            return left > 0 ? left : 0;
        },
        // 还原
        docRestore(row) {
            return request({
                url: '/doc/restore',
                method: 'post',
                data: {
                    id: row.id.toString()
                }
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({
                        message: res.msg || '操作成功',
                        type: 'success',
                        duration: 5 * 1000
                    });

                    this.loadDeletedDoc();
                } else {
                    ElMessage({
                        message: res.msg || 'Error',
                        type: 'error',
                        duration: 5 * 1000
                    });
                }
            });
        },
        // 还原
        fileRestore(row) {
            return request({
                url: '/file/restore',
                method: 'post',
                data: {
                    id: row.id.toString()
                }
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({
                        message: res.msg || '操作成功',
                        type: 'success',
                        duration: 5 * 1000
                    });

                    this.loadDeletedFile();
                } else {
                    ElMessage({
                        message: res.msg || 'Error',
                        type: 'error',
                        duration: 5 * 1000
                    });
                }
            });
        }
    }
};
</script>

<style scoped>
.recycle-page {
    /* 局部覆盖主色，与其它工作台页面保持同一套视觉语言 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --recycle-text: #1f2d3d;
    --recycle-sub: #5b6a80;
    --recycle-muted: #aab4c4;
    --recycle-line: #eef1f6;
    --recycle-border: #e6eaf0;

    color: var(--recycle-text);
}

/* ---------- 工具条 ---------- */
.recycle-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    row-gap: 10px;
    padding: 10px 16px;
    border: 1px solid #ebeef5;
    border-radius: 12px;
    background: linear-gradient(180deg, #ffffff 0%, #fafbfd 100%);
    box-shadow: 0 10px 28px -22px rgba(31, 45, 61, 0.55);
}

.recycle-toolbar__info {
    display: flex;
    align-items: center;
    min-width: 0;
}

.recycle-toolbar__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-right: 10px;
    border-radius: 10px;
    font-size: 18px;
    color: #fff;
    background: linear-gradient(135deg, #ff9d9d 0%, #f56c6c 100%);
    box-shadow: 0 8px 16px -10px rgba(245, 108, 108, 0.9);
}

.recycle-toolbar__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.recycle-toolbar__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 20px;
    color: #1f2d3d;
}

.recycle-toolbar__subtitle {
    font-size: 12px;
    line-height: 16px;
    color: #a8abb2;
}

.recycle-toolbar__actions {
    display: flex;
    flex: none;
    flex-wrap: wrap;
    align-items: center;
    row-gap: 8px;
}

/* ---------- 工具条按钮 ---------- */
.toolbar-btn {
    height: 34px;
    padding: 0 16px;
    border-radius: 9px;
    font-weight: 500;
    transition: all 0.2s ease;
}

.toolbar-btn--ghost {
    color: #606266;
    background-color: #fff;
    border-color: #e4e7ed;
}

.toolbar-btn--ghost:hover,
.toolbar-btn--ghost:focus {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
    border-color: var(--el-color-primary-light-5);
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -12px rgba(47, 111, 237, 0.9);
}

.btn-icon {
    margin-right: 4px;
}

/* ---------- 分类切换（文档 / 文件） ---------- */
.recycle-switch {
    display: flex;
    gap: 8px;
    margin-top: 14px;
}

.recycle-switch__item {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 18px;
    border: 1px solid var(--recycle-border);
    border-radius: 10px;
    background-color: #fff;
    color: var(--recycle-sub);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
}

.recycle-switch__item:hover {
    color: var(--el-color-primary);
    border-color: var(--el-color-primary-light-5);
}

.recycle-switch__item.is-active {
    color: #fff;
    border-color: transparent;
    background: linear-gradient(135deg, #2f6fed 0%, #2659be 100%);
    box-shadow: 0 10px 20px -12px rgba(47, 111, 237, 1);
}

.recycle-switch__icon {
    font-size: 16px;
}

.recycle-switch__count {
    padding: 0 8px;
    border-radius: 999px;
    background-color: #f2f5fa;
    color: var(--recycle-sub);
    font-size: 12px;
    font-style: normal;
    line-height: 18px;
}

.recycle-switch__item.is-active .recycle-switch__count {
    background-color: rgba(255, 255, 255, 0.24);
    color: #fff;
}

/* ---------- 搜索与统计 ---------- */
.recycle-filter {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
}

.recycle-search {
    flex: 0 1 320px;
    min-width: 180px;
}

.recycle-search :deep(.el-input__wrapper) {
    border-radius: 10px;
    background-color: #fff;
    box-shadow: 0 0 0 1px var(--recycle-border) inset;
    transition: box-shadow 0.2s;
}

.recycle-search :deep(.el-input__wrapper:hover) {
    box-shadow: 0 0 0 1px #cfd8e6 inset;
}

.recycle-search :deep(.el-input__wrapper.is-focus) {
    box-shadow: 0 0 0 1px var(--el-color-primary) inset;
}

.recycle-filter__count {
    font-size: 12px;
    color: var(--recycle-muted);
}

/* ---------- 记录列表 ---------- */
.recycle-panel {
    margin-top: 12px;
    padding: 6px;
    border: 1px solid var(--recycle-border);
    border-radius: 14px;
    background-color: #fff;
    box-shadow: 0 1px 2px rgba(28, 47, 84, 0.05);
}

.recycle-panel :deep(.el-empty) {
    padding: 40px 0;
}

.recycle-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 10px;
    transition: background-color 0.2s;
}

.recycle-item+.recycle-item {
    border-top: 1px solid var(--recycle-line);
}

.recycle-item:hover {
    background-color: #f7faff;
}

.recycle-item__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 10px;
    font-size: 17px;
    background-color: #f2f5fa;
    color: #8a94a6;
}

.recycle-item__icon--doc {
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
}

.recycle-item__icon--file {
    background-color: #fff7e6;
    color: #e6a23c;
}

.recycle-item__main {
    flex: 1 1 auto;
    min-width: 0;
}

.doc-ai-mark {
    display: inline-block;
    margin-right: 6px;
    padding: 0 4px;
    border-radius: 4px;
    background: #eaf1fd;
    color: #2f6fed;
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 16px;
    vertical-align: 1px;
}
.recycle-item__name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 14px;
    font-weight: 600;
    color: var(--recycle-text);
}

.recycle-item__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
    font-size: 12px;
    color: var(--recycle-muted);
}

.recycle-chip {
    padding: 1px 8px;
    border-radius: 999px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    font-size: 12px;
}

.recycle-chip--file {
    background-color: #fff7e6;
    color: #e6a23c;
}

.recycle-item__times {
    display: flex;
    flex: none;
    flex-direction: column;
    align-items: flex-end;
    gap: 3px;
    font-size: 12px;
    color: var(--recycle-sub);
}

.recycle-time {
    white-space: nowrap;
}

.recycle-time--delete {
    color: #e6a23c;
}

.recycle-item__actions {
    display: flex;
    flex: none;
    align-items: center;
    gap: 10px;
}

/* 剩余保留天数 */
.recycle-left {
    padding: 1px 8px;
    border-radius: 999px;
    background-color: #f2f5fa;
    color: var(--recycle-muted);
    font-size: 12px;
    white-space: nowrap;
}

.recycle-left.is-urgent {
    background-color: #fef0f0;
    color: #f56c6c;
}

.restore-btn {
    border-radius: 8px;
}

/* ---------- 小尺寸设备适配 ---------- */
@media (max-width: 768px) {
    .recycle-switch {
        width: 100%;
    }

    .recycle-switch__item {
        flex: 1 1 0;
        justify-content: center;
        padding: 0 10px;
    }

    .recycle-filter {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .recycle-search {
        flex: 1 1 auto;
        width: 100%;
    }

    /* 窄屏下每条记录改为「名称 / 时间 / 操作」三行 */
    .recycle-item {
        flex-wrap: wrap;
        row-gap: 8px;
        padding: 12px;
    }

    .recycle-item__times {
        flex: 1 1 100%;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
    }

    .recycle-item__actions {
        flex: 1 1 100%;
        justify-content: flex-end;
    }
}
</style>
